import Image from 'next/image';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DealSkuCardType } from '~/components/sku';
import { Heading } from '~/components/ui';
import TableOfContent, {
  TableOfContentItemProp,
} from '~/components/ui/TableOfContent';
import { SmartCollectionContext } from '~/contexts/smart-collection';
import { Attribute } from '~/types/deal';

interface DealProps {
  id: string | number;
  public_name: string;
  media_url: string;
  sku: {
    price: number;
  };
  story: {
    card_description: string;
    highlights: { highlight: string }[];
    snippet?: string;
    snippet_seo?: string;
    bottom_header?: string;
  };
  common_features?: { text: string }[];
  features?: { feature: string }[];
}

interface DealSourceProps extends DealProps {
  attributes?: Attribute[];
}

interface AlternativeCollectionProps {
  data_source_object: DealSourceProps;
  deals: DealProps[];
  title: string;
  subheader: string;
  description: string;
}

export const Alternative = ({
  collection,
}: {
  collection: AlternativeCollectionProps;
}) => {
  const { data_source_object: deal } = collection;
  const [now] = useState(new Date());
  const [enableCollapse, setEnableCollapse] = useState(false);
  const tableContentSideBar = useRef<HTMLDivElement>(null);
  const { deals } = useContext(SmartCollectionContext);

  const linksItems: TableOfContentItemProp[] = useMemo(() => {
    return deals.map((deal: DealSkuCardType) => {
      const item: TableOfContentItemProp = {
        label: deal.public_name,
        id: deal?.slug,
        children: [],
      };

      if (deal?.reviews_summary) {
        item.children?.push({
          label: `What do users say about ${deal?.public_name}`,
          id: `what_users_say_${deal?.slug}`,
        });
      }

      if (deal.common_features.length) {
        item.children?.push({
          label: 'Top features',
          id: `top_features_${deal?.slug}`,
        });
      }

      if (deal.snippet_seo) {
        item.children?.push({
          label: `What is ${deal?.public_name}?`,
          id: `what_is_${deal?.slug}`,
        });
      }

      return item;
    });
  }, [deals]);

  const toggleCollapse = useCallback((e: MediaQueryListEvent) => {
    setEnableCollapse(e.matches);
  }, []);

  useEffect(() => {
    const mobileQuery = window.matchMedia('(max-width: 1024px)');

    setEnableCollapse(mobileQuery.matches);
    mobileQuery.addEventListener('change', toggleCollapse);

    return () => {
      mobileQuery.removeEventListener('change', toggleCollapse);
    };
  }, [toggleCollapse]);

  if (!deal) return <></>;

  return (
    <div className="text-left">
      <div className="mx-auto flex flex-col gap-6 lg:max-w-screen-2xl lg:flex-row">
        <div>
          <div className="flex flex-col gap-y-6">
            <Heading.H1 className="lg:text-[42px] lg:leading-none">
              {collection.title} ({new Date().getFullYear()})
            </Heading.H1>

            <div className="flex flex-col gap-4 sm:gap-y-8 xl:flex-row">
              <div className="w-full shrink-0 overflow-hidden rounded md:w-2/5 xl:max-w-md">
                <Image
                  src={`${deal.media_url}?width=320&height=180`}
                  width={320}
                  height={180}
                  unoptimized
                  alt={deal.public_name}
                  className="aspect-video w-full object-cover"
                />
              </div>
              <div>
                {deal?.story?.snippet_seo && (
                  <div
                    className="space-y-2 break-words md:grow"
                    dangerouslySetInnerHTML={{
                      __html: deal?.story?.snippet_seo,
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="mt-8 border-t border-sundog pt-4">
            <Heading.H2 className="lg:text-3xl">
              Top {deals.length} Alternatives to {deal.public_name} in{' '}
              {now.getFullYear()}
            </Heading.H2>
            <div
              className="mt-1 text-grace"
              dangerouslySetInnerHTML={{ __html: collection.description }}
            />
          </div>
        </div>

        <div
          ref={tableContentSideBar}
          className="relative w-full max-w-sm shrink-0 max-lg:order-first"
        >
          <div
            className="fixed left-0 z-1 bg-white max-lg:right-0 max-lg:-mt-6 max-lg:!w-full lg:left-auto lg:top-auto"
            style={{ width: tableContentSideBar?.current?.clientWidth }}
          >
            <TableOfContent
              title="On this page"
              items={linksItems}
              enableCollapse={enableCollapse}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
